import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  RedirectToSignIn,
  SignIn,
  SignUp,
  useAuth,
} from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import OperacionesLP from "@/pages/OperacionesLP";
import OperacionesCP from "@/pages/OperacionesCP";
import Empresas from "@/pages/Empresas";
import Configuracion from "@/pages/Configuracion";
import Proyecciones from "@/pages/Proyecciones";
import Invitaciones from "@/pages/Invitaciones";
import { EmpresaProvider } from "@/context/EmpresaContext";

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Adjunta el JWT de sesión Clerk como Bearer en cada request de axios.
function AxiosClerkBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    const id = axios.interceptors.request.use(async (config) => {
      try {
        const token = await getToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        // Sin token: el backend responderá 401.
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [getToken]);
  return null;
}

function ClerkProviderWithRouter({ children }) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
    >
      {children}
    </ClerkProvider>
  );
}

function AuthCenter({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      {children}
    </div>
  );
}

// Layout route: exige sesión; si no hay, redirige al login de Clerk.
function RequireAuth() {
  return (
    <>
      <SignedIn>
        <EmpresaProvider>
          <Outlet />
        </EmpresaProvider>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function App() {
  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: 40, fontFamily: "sans-serif" }}>
        <h1>Configuración incompleta</h1>
        <p>
          Falta la variable <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> en el
          build del frontend.
        </p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ClerkProviderWithRouter>
        <AxiosClerkBridge />
        <Routes>
          <Route
            path="/sign-in/*"
            element={
              <AuthCenter>
                <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />
              </AuthCenter>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthCenter>
                <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
              </AuthCenter>
            }
          />
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="operaciones-lp" element={<OperacionesLP />} />
              <Route path="operaciones-cp" element={<OperacionesCP />} />
              <Route path="proyecciones" element={<Proyecciones />} />
              <Route path="empresas" element={<Empresas />} />
              <Route path="invitaciones" element={<Invitaciones />} />
              <Route path="configuracion" element={<Configuracion />} />
            </Route>
          </Route>
        </Routes>
        <Toaster position="top-right" richColors />
      </ClerkProviderWithRouter>
    </BrowserRouter>
  );
}

export default App;
